import { type DocumentNode, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type {
  AccountingFirmRoleInput,
  UserCompanyRoleInput,
  UserQueryData,
} from "./types";

export const CREATE_ACCOUNTING_FIRM_USER_MUTATION = gql`
  mutation CreateAccountingFirmUser($userData: AccountingFirmUserInput!) {
    createAccountingFirmUser(userData: $userData) {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
      companies {
        companyId
        companyName
        role
      }
    }
  }
`;

export const CREATE_COMPANY_USER_MUTATION = gql`
  mutation CreateCompanyUser($userData: CompanyUserInput!) {
    createCompanyUser(userData: $userData) {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
      companies {
        companyId
        companyName
        role
      }
    }
  }
`;

type CreateAccountingFirmUserInput = {
  userData: {
    name: string;
    email: string;
    accountingFirmRole?: AccountingFirmRoleInput;
    companyAccess?: UserCompanyRoleInput[];
  };
};

type CreateCompanyUserInput = {
  userData: {
    name: string;
    email: string;
    companyAccess?: UserCompanyRoleInput[];
  };
};

type RefetchEntry =
  | DocumentNode
  | { query: DocumentNode; variables?: Record<string, any> };

export const useCreateAccountingFirmUser = (
  refetchQueries: RefetchEntry[] = [],
) => {
  const { t } = useTranslation();

  const [createUser, { data, loading, error }] = useMutation<UserQueryData>(
    CREATE_ACCOUNTING_FIRM_USER_MUTATION,
    {
      refetchQueries: refetchQueries.map((entry) =>
        "query" in (entry as any)
          ? (entry as any)
          : { query: entry as DocumentNode },
      ),
    },
  );

  const handleCreateAccountingFirmUser = async (
    data: CreateAccountingFirmUserInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await createUser({
        variables: {
          userData: data.userData,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return [handleCreateAccountingFirmUser, { data, loading, error }] as const;
};

export const useCreateCompanyUser = (refetchQueries: RefetchEntry[] = []) => {
  const { t } = useTranslation();

  const [createUser, { data, loading, error }] = useMutation<UserQueryData>(
    CREATE_COMPANY_USER_MUTATION,
    {
      refetchQueries: refetchQueries.map((entry) =>
        "query" in (entry as any)
          ? (entry as any)
          : { query: entry as DocumentNode },
      ),
    },
  );

  const handleCreateCompanyUser = async (
    data: CreateCompanyUserInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await createUser({
        variables: {
          userData: data.userData,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return [handleCreateCompanyUser, { data, loading, error }] as const;
};
